import React from 'react';
import { motion } from 'framer-motion';

// Crying emojis floating/dripping down subtly from top and sides of the pill
const TOP_SADNESS_TEARS = [
  { x: -65, startY: -55, endY: -18, icon: '😢', size: 23, delay: 0.2, duration: 1.8 },
  { x: 65, startY: -55, endY: -18, icon: '😢', size: 23, delay: 0.9, duration: 1.8 },
];

const SIDE_SADNESS_TEARS = [
  // Left side
  { x: -170, startY: -8, endY: 52, icon: '😢', size: 25, delay: 0.5, duration: 1.9 },
  // Right side
  { x: 170, startY: -8, endY: 52, icon: '😢', size: 25, delay: 1.2, duration: 1.9 },
];

const ALL_SADNESS_TEARS = [...TOP_SADNESS_TEARS, ...SIDE_SADNESS_TEARS];

interface PillSadnessEffectProps {
  variant?: 'set' | 'opponents_bid';
}

export const PillSadnessEffect: React.FC<PillSadnessEffectProps> = ({ variant = 'set' }) => {
  const primaryRingBorder = variant === 'opponents_bid' ? 'border-rose-500/50' : 'border-red-500/50';
  const secondaryRingBorder = variant === 'opponents_bid' ? 'border-sky-400/40' : 'border-sky-500/35';

  return (
    <div
      className="absolute -inset-14 sm:-inset-18 pointer-events-none overflow-visible flex items-center justify-center z-30 select-none bg-transparent"
      aria-hidden="true"
    >
      {/* Dissipating somber gloomy ripple rings around pill */}
      <motion.div
        initial={{ scale: 0.92, opacity: 0.7 }}
        animate={{
          scale: [0.92, 1.2, 1.3],
          opacity: [0.7, 0.25, 0],
          y: [0, 3, 6],
        }}
        transition={{
          duration: 1.8,
          repeat: Infinity,
          ease: 'easeOut',
        }}
        className={`absolute inset-0 rounded-full border-2 ${primaryRingBorder} pointer-events-none bg-transparent`}
      />
      <motion.div
        initial={{ scale: 0.95, opacity: 0.5 }}
        animate={{
          scale: [0.95, 1.12, 1.22],
          opacity: [0.5, 0.15, 0],
          y: [0, 2, 4],
        }}
        transition={{
          duration: 1.8,
          repeat: Infinity,
          delay: 0.5,
          ease: 'easeOut',
        }}
        className={`absolute inset-0 rounded-full border ${secondaryRingBorder} pointer-events-none bg-transparent`}
      />

      {/* Crying emojis cascading down from top and sides */}
      {ALL_SADNESS_TEARS.map((tear, idx) => (
        <motion.div
          key={`tear-${idx}`}
          initial={{ x: tear.x, y: tear.startY, scale: 0.5, opacity: 0 }}
          animate={{
            x: [tear.x, tear.x - 1, tear.x + 1, tear.x],
            y: [tear.startY, tear.startY + 14, tear.endY],
            scale: [0.5, 1.1, 0.85],
            opacity: [0, 0.95, 0],
          }}
          transition={{
            duration: tear.duration,
            repeat: Infinity,
            delay: tear.delay,
            ease: 'easeIn',
          }}
          className="absolute text-center leading-none pointer-events-none select-none bg-transparent drop-shadow-[0_2px_8px_rgba(56,189,248,0.55)]"
          style={{
            fontSize: `${tear.size}px`,
          }}
        >
          {tear.icon}
        </motion.div>
      ))}
    </div>
  );
};
